import { createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery, endpoints } from 'utils/api/endpoints';

export interface State {}

export const initialState: State = {};

const slice = createSlice({
  name: 'taxManagement',
  initialState,
  reducers: {},
});

export const api = createApi({
  reducerPath: 'taxManagementApi',
  baseQuery,
  tagTypes: ['Tax'],
  endpoints: builder => ({
    getTaxes: builder.query<any, any>({
      query: (params: any = {}) => {
        return {
          url: endpoints.TAXES,
          method: 'GET',
          params,
        };
      },
      providesTags: ['Tax'],
    }),
    addTax: builder.mutation<any, any>({
      query: (data: any) => {
        return {
          url: endpoints.ADD_TAX,
          method: 'POST',
          body: data,
        };
      },
      invalidatesTags: ['Tax'],
    }),
    deleteTax: builder.mutation<any, any>({
      query: (id: number) => {
        return {
          url: endpoints.DELETE_TAX + '/' + id,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['Tax'],
    }),
    getPaymentMethods: builder.query<any, any>({
      query: (params: any = {}) => {
        return {
          url: endpoints.PAYMENT_GATEWAYS + '/active',
          method: 'GET',
          params,
        };
      },
    }),
    updateStatus: builder.mutation<any, any>({
      query: ({ id, ...data }) => {
        return {
          url: `${endpoints.TAXES}/${id}/status`,
          method: 'PATCH',
          body: data,
        };
      },
      invalidatesTags: ['Tax'],
    }),
    updateTax: builder.mutation<any, any>({
      query: ({ id, ...data }) => {
        return {
          url: `${endpoints.TAXES}/${id}/update`,
          method: 'PATCH',
          body: data,
        };
      },
      invalidatesTags: ['Tax'],
    }),
  }),
});

export const { actions: taxManagementActions } = slice;

// Export hooks for usage in functional components
export const {
  useGetTaxesQuery,
  useAddTaxMutation,
  useDeleteTaxMutation,
  useGetPaymentMethodsQuery,
  useUpdateStatusMutation,
  useUpdateTaxMutation,
} = api;

// Export the slice reducer
export const { reducer } = slice;

// Export the API slice
export const { reducer: apiReducer } = api;

// Hook for injecting the slice
export const useTaxManagementSlice = () => {
  const { useInjectReducer } = require('utils/redux-injectors');
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return {
    actions: slice.actions,
    useGetTaxesQuery: api.useLazyGetTaxesQuery,
    useAddTaxMutation,
    useDeleteTaxMutation,
    useGetPaymentMethodsQuery: api.useLazyGetPaymentMethodsQuery,
    useUpdateStatusMutation,
    useUpdateTaxMutation,
  };
};
